import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useAuth0, User } from '../Auth0Provider';
import { initializeFirebase } from '../firebaseConfig';

const db = initializeFirebase().firestore();

//todo: get types auto-generated
export interface UserDto {
  id: string;
  displayName: string;
  picture: string;
}

export interface ChatMessageDto {
  text: string;
  author: UserDto;
  timestamp: Date;
  isSystemMessage?: boolean;
}

export interface ChatMessageGroup {
  author: UserDto;
  messages: string[];
  timestamp: Date;
  isSystemMessage?: boolean;
}

export const useChat = (hackathonId: number) => {
  const { isAuthenticated, user } = useAuth0();
  const [messageGroups, dispatch] = useReducer(messagesReducer, []);
  const collectionName = `hackathon-${hackathonId}`;

  useEffect(() => {
    db.collection(collectionName)
      .orderBy('timestamp')
      .onSnapshot((snapshot) => {
        const messages = snapshot.docChanges().map((change) => parseChatMessage(change.doc.data()));

        messages.forEach((message) => dispatch({ type: 'MESSAGE_RECEIVED', message }));
      });
  }, [collectionName]);

  const canSend = userCanSend(isAuthenticated, user);
  const sendMessage = useCallback(
    (message: string) => {
      if (!canSend) return;

      const chatMessage: ChatMessageDto = {
        author: {
          displayName: user.name,
          id: user.sub,
          picture: user.picture,
        },
        text: message,
        timestamp: new Date(),
      };

      return db.collection(collectionName).add(chatMessage);
    },
    [collectionName, canSend, user]
  );

  return useMemo(() => ({ messageGroups, sendMessage, canSend }), [messageGroups, sendMessage, canSend]);
};

function parseChatMessage(firebaseDoc: any): ChatMessageDto {
  return {
    ...firebaseDoc,
    timestamp: new Date(firebaseDoc.timestamp.seconds * 1000),
  };
}

function groupMessages(messages: ChatMessageDto[]) {
  const groups: ChatMessageGroup[] = [];
  let currentGroup: ChatMessageGroup;
  messages.forEach((message) => {
    if (!currentGroup || currentGroup.author.id !== message.author.id) {
      currentGroup = { ...message, messages: [] };
      groups.push(currentGroup);
    }

    currentGroup.messages.push(message.text);
    currentGroup.timestamp = message.timestamp;
  });
  return groups;
}

function userCanSend(isAuthenticated: boolean, user: User | undefined): user is User {
  return !!user && isAuthenticated;
}

interface InitialMessagesReceived {
  type: 'INITIAL_MESSAGES_RECEIVED';
  messages: ChatMessageDto[];
}

interface MessageReceived {
  type: 'MESSAGE_RECEIVED';
  message: ChatMessageDto;
}

type MessageActions = InitialMessagesReceived | MessageReceived;

const messagesReducer = (messageGroups: ChatMessageGroup[], action: MessageActions): ChatMessageGroup[] => {
  switch (action.type) {
    case 'INITIAL_MESSAGES_RECEIVED':
      return groupMessages(action.messages);
    case 'MESSAGE_RECEIVED':
      const currentGroup = messageGroups[messageGroups.length - 1];
      const isNewMessageForCurrentGroup = currentGroup && currentGroup.author.id === action.message.author.id;
      if (!isNewMessageForCurrentGroup) return [...messageGroups, createNewGroup(action.message)];

      const otherGroups = messageGroups.slice(0, messageGroups.length - 1);
      return [...otherGroups, createUpdatedGroup(currentGroup, action.message)];

    default:
      return messageGroups;
  }
};

const createNewGroup = (message: ChatMessageDto): ChatMessageGroup => ({
  ...message,
  messages: [message.text],
});

const createUpdatedGroup = (group: ChatMessageGroup, message: ChatMessageDto): ChatMessageGroup => ({
  ...group,
  timestamp: message.timestamp,
  messages: [...group.messages, message.text],
});
